import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Plus, Trash2, Store, MapPin, Loader2 } from 'lucide-react';
import {
  type Stall,
  type ShippingZone,
  CURRENCIES,
  SHIPPING_REGIONS,
  generateId,
} from '@/lib/marketplace';

interface StallFormProps {
  initial?: Stall;
  onSave: (stall: Stall) => Promise<void>;
  onCancel?: () => void;
  isSaving?: boolean;
}

const emptyZone = (): ShippingZone => ({
  id: generateId(),
  name: '',
  cost: 0,
  regions: [],
});

export function StallForm({ initial, onSave, onCancel, isSaving }: StallFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [currency, setCurrency] = useState(initial?.currency ?? 'SATS');
  const [zones, setZones] = useState<ShippingZone[]>(
    initial?.shipping?.length ? initial.shipping : [emptyZone()]
  );

  const addZone = () => setZones(z => [...z, emptyZone()]);
  const removeZone = (idx: number) => setZones(z => z.filter((_, i) => i !== idx));

  const updateZone = (idx: number, patch: Partial<ShippingZone>) =>
    setZones(z => z.map((zone, i) => i === idx ? { ...zone, ...patch } : zone));

  const toggleRegion = (zoneIdx: number, region: string) => {
    const zone = zones[zoneIdx];
    const next = zone.regions.includes(region)
      ? zone.regions.filter(r => r !== region)
      : [...zone.regions, region];
    updateZone(zoneIdx, { regions: next });
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    const stall: Stall = {
      id: initial?.id ?? generateId(),
      name: name.trim(),
      description: description.trim() || undefined,
      currency,
      shipping: zones.filter(z => z.regions.length > 0 || z.cost === 0),
    };
    await onSave(stall);
  };

  return (
    <div className="space-y-6">
      {/* Basic info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Store className="h-4 w-4 text-primary" />
            Stall Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Stall Name *</Label>
            <Input
              placeholder="e.g. Bitcoin Hardware Store"
              value={name}
              onChange={e => setName(e.target.value)}
              className="h-9"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Description</Label>
            <Textarea
              placeholder="Describe your stall and what you sell…"
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              className="text-sm resize-none"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Currency *</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map(c => (
                  <SelectItem key={c.value} value={c.value} className="text-sm">
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Shipping zones */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" />
              Shipping Zones
            </CardTitle>
            <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={addZone}>
              <Plus className="h-3 w-3" />
              Add Zone
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {zones.map((zone, idx) => (
            <div key={zone.id} className="border rounded-lg p-3 space-y-3 bg-muted/30">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Zone {idx + 1}
                </span>
                {zones.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => removeZone(idx)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Zone Name</Label>
                  <Input
                    placeholder="e.g. Worldwide"
                    value={zone.name ?? ''}
                    onChange={e => updateZone(idx, { name: e.target.value })}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Base Cost ({currency})</Label>
                  <Input
                    type="number"
                    min="0"
                    step="any"
                    placeholder="0"
                    value={zone.cost}
                    onChange={e => updateZone(idx, { cost: parseFloat(e.target.value) || 0 })}
                    className="h-8 text-xs"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Regions (select all that apply)</Label>
                <div className="flex flex-wrap gap-1.5">
                  {SHIPPING_REGIONS.map(region => {
                    const selected = zone.regions.includes(region);
                    return (
                      <button
                        key={region}
                        type="button"
                        onClick={() => toggleRegion(idx, region)}
                        className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                          selected
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'border-border text-muted-foreground hover:border-primary hover:text-foreground'
                        }`}
                      >
                        {region}
                      </button>
                    );
                  })}
                </div>
                {zone.regions.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {zone.regions.map(r => (
                      <Badge key={r} variant="secondary" className="text-[10px] px-1.5 py-0">
                        {r}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Separator />

      <div className="flex items-center justify-between gap-3 pt-1">
        {onCancel && (
          <Button variant="outline" size="sm" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSave}
          disabled={!name.trim() || isSaving}
          size="sm"
          className="gap-2 ml-auto"
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Store className="h-4 w-4" />}
          {initial ? 'Update Stall' : 'Publish Stall'}
        </Button>
      </div>
    </div>
  );
}
